import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, Package, ChevronRight, ChevronLeft, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { LowStockItem } from '../../hooks/useLowStockAlerts';

interface LowStockAlertsProps {
  data: LowStockItem[] | undefined;
  total?: number;
  isLoading?: boolean;
  isRefetching?: boolean;
  onRefresh?: () => void;
  className?: string;
}

const statusColors: Record<LowStockItem['status'], { bg: string; text: string; icon: string }> = {
  critical: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-700 dark:text-red-400',
    icon: 'text-red-500 dark:text-red-400',
  },
  low: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    text: 'text-yellow-700 dark:text-yellow-400',
    icon: 'text-yellow-500 dark:text-yellow-400',
  },
  warning: {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    text: 'text-orange-700 dark:text-orange-400',
    icon: 'text-orange-500 dark:text-orange-400',
  },
};

function LowStockAlertsComponent({
  data,
  total = 0,
  isLoading = false,
  isRefetching = false,
  onRefresh,
  className,
}: LowStockAlertsProps) {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const isRTL = i18n.dir() === 'rtl';
  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div
      className={cn(
        'bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 shadow-sm flex flex-col',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 p-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
              {t('alerts.lowStock')}
            </h3>
            {!isLoading && total > 0 && (
              <p className="text-sm text-secondary-500 dark:text-secondary-400">
                {t('alerts.lowStockCount', { count: total })}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefetching}
              className={cn(
                'p-2 rounded-lg text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100',
                'dark:text-secondary-400 dark:hover:text-secondary-200 dark:hover:bg-secondary-700',
                'transition-colors disabled:opacity-50'
              )}
              aria-label={t('common:refresh')}
            >
              <RefreshCw
                className={cn('h-4 w-4', isRefetching && 'animate-spin')}
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-lg bg-secondary-50 dark:bg-secondary-700/50 animate-pulse"
              >
                <div className="w-10 h-10 rounded-lg bg-secondary-200 dark:bg-secondary-600" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-secondary-200 dark:bg-secondary-600 rounded w-3/4" />
                  <div className="h-3 bg-secondary-200 dark:bg-secondary-600 rounded w-1/2" />
                </div>
                <div className="w-16 h-6 bg-secondary-200 dark:bg-secondary-600 rounded" />
              </div>
            ))}
          </div>
        ) : data && data.length > 0 ? (
          <ul className="divide-y divide-secondary-100 dark:divide-secondary-700" role="list">
            {data.map((item) => {
              const colors = statusColors[item.status];
              return (
                <li key={item.id}>
                  <Link
                    to={`/inventory/items/${item.itemCode}`}
                    className={cn(
                      'flex items-center gap-3 px-6 py-3 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors'
                    )}
                  >
                    <div className={cn('rounded-lg p-2', colors.bg)}>
                      <Package className={cn('h-5 w-5', colors.icon)} aria-hidden="true" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100 truncate">
                        {item.itemName}
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                        {item.outletName} - {item.category}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <p className={cn('text-sm font-semibold', colors.text)}>
                          {item.currentQty} / {item.minimumQty}
                        </p>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">
                          {item.unit}
                        </p>
                      </div>
                      <ChevronIcon
                        className="h-4 w-4 text-secondary-400 dark:text-secondary-500"
                        aria-hidden="true"
                      />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
            <div className="rounded-full bg-green-50 dark:bg-green-900/20 p-3 mb-3">
              <Package className="h-8 w-8 text-green-500 dark:text-green-400" aria-hidden="true" />
            </div>
            <p className="text-secondary-600 dark:text-secondary-400 font-medium">
              {t('alerts.noLowStock')}
            </p>
            <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
              {t('alerts.allItemsStocked')}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      {data && data.length > 0 && total > data.length && (
        <div className="px-6 py-3 border-t border-secondary-200 dark:border-secondary-700">
          <Link
            to="/inventory/low-stock"
            className={cn(
              'flex items-center justify-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700',
              'dark:text-primary-400 dark:hover:text-primary-300 transition-colors'
            )}
          >
            {t('alerts.viewAll', { count: total })}
            <ChevronIcon className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      )}
    </div>
  );
}

export const LowStockAlerts = memo(LowStockAlertsComponent);
export default LowStockAlerts;
